// Typed wrappers around the /api/tests backend endpoints.
//
// Wire shape is validated through the Zod schemas in ./schemas — the
// schemas in turn are kept in lockstep with the Pydantic response
// models in damperlib/api_models.py via the contract tests
// (tests/test_api_contract.py + frontend/tests/api-contract.test.ts).
import { z } from "zod";
import {
  TestSchema,
  TestListItemSchema,
  type Test,
  type TestListItem,
} from "./schemas";

const TestListSchema = z.array(TestListItemSchema);

// Re-export the canonical types so existing callers (components +
// tests) can continue to import { Test } from "../api/tests" without
// reaching across modules.
export type { Test, TestListItem };
export type Run = import("./schemas").Run;
export type RunArtifactRef = import("./schemas").RunArtifactRef;
export type ProvenanceSummary = import("./schemas").ProvenanceSummary;
export type ProvenanceInputStatus = import("./schemas").ProvenanceInputStatus;

// KpiTarget is a frontend-only refinement of the per-key value inside
// `kpi_targets` (which the backend stores as opaque JSON). Components
// that read .direction / .target cast through this type.
export type KpiTarget = {
  direction: "max" | "min";
  target: number;
};

// Item D: a Test that drives the rig from an ingested track-replay lap carries
// this under kpi_targets.track_replay (the backend reads it there). track_code
// is the replay CACHE key (the track itself, not the campaign source_event's
// cache prefix).
export type TrackReplayConfig = {
  enabled: boolean;
  track_code: string;
  stem?: string;
  duration_s?: number;
};

// Slice 3: create-time tyre override, stored under kpi_targets.tire_override.
export type TireOverrideConfig = {
  type: string;
};

// Fable P2: a replay-only Study (e.g. RAM) stamps its setup-donor campaign
// under kpi_targets.setup_donor — provenance for "this car platform came
// from another event's setup XLSM; rig actuation came from the lap".
export type SetupDonorConfig = {
  source_event: string;
  reason: string;
};

// Fable P4: a replay-only Study backed by the venue's OWN staged setup
// stores the staged-setup id under kpi_targets.setup_file ("<season>/<name>")
// — no donor source_event needed; the runner resolves the staged XLSM.
export type SetupFileId = string;

// Lever-set v1 (2026-07-12): a study may opt AutoResearch into extra levers
// beyond dampers, stored under kpi_targets.ar_levers. Exactly two are in scope:
// "tire" (per-candidate tyre-catalogue choice) and "third_gap" (per-candidate
// third-element gap). Absent/empty = dampers-only. The backend 422s any other
// entry; the driver enforces per-candidate availability against this set.
export type AutoResearchLever = "tire" | "third_gap";

export type CreateTestBody = {
  track_code: string;
  replay_recording_id?: number | null;
  baseline_setup_name?: string | null;
  kpi_targets: Record<
    string,
    | KpiTarget
    | TrackReplayConfig
    | TireOverrideConfig
    | SetupDonorConfig
    | SetupFileId
    | AutoResearchLever[]
  >;
  // Phase 1C: the wizard's cached-event picker sets this so the
  // Phase 1A runner can resolve inputs for any subsequent Run on
  // the Test. Optional for backward-compat with older callers.
  source_event?: string | null;
  name?: string | null;
};

export async function fetchTests(): Promise<TestListItem[]> {
  const r = await fetch("/api/tests");
  if (!r.ok) throw new Error(`GET /api/tests failed: ${r.status}`);
  return TestListSchema.parse(await r.json());
}

export async function fetchTest(id: number): Promise<Test> {
  const r = await fetch(`/api/tests/${id}`);
  if (!r.ok) throw new Error(`GET /api/tests/${id} failed: ${r.status}`);
  return TestSchema.parse(await r.json());
}

export async function createTest(body: CreateTestBody): Promise<Test> {
  const r = await fetch("/api/tests", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`POST /api/tests failed: ${r.status}: ${text}`);
  }
  return TestSchema.parse(await r.json());
}

export type UpdateTestBody = {
  name?: string | null;
};

export async function updateTest(
  id: number,
  body: UpdateTestBody,
): Promise<Test> {
  const r = await fetch(`/api/tests/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`PATCH /api/tests/${id} failed: ${r.status}: ${text}`);
  }
  return TestSchema.parse(await r.json());
}

// Item D: tracks with at least one ingested track-replay lap in the local
// cache. Powers the wizard's "drive from track-replay lap" option. Phase 4:
// `available` is the explicit deployment-level contract — False on a deployment
// with no laps (e.g. cloud) so the wizard can show an explicit "unavailable
// here" state instead of silently omitting the mode.
const TrackReplayStatusSchema = z.object({
  available: z.boolean().optional().default(false),
  tracks: z.array(
    z.object({
      track_code: z.string(),
      stems: z.array(z.string()),
      // Campaign MAT-prefix aliases resolving to this cache dir (mid→MOH,
      // lag→LSE). Optional: payloads predating 2026-07-02 lack it.
      campaign_codes: z.array(z.string()).optional().default([]),
    }),
  ),
});
export type TrackReplayStatus = {
  available: boolean;
  tracks: { track_code: string; stems: string[]; campaign_codes: string[] }[];
};
export async function fetchTrackReplayStatus(): Promise<TrackReplayStatus> {
  const r = await fetch("/api/track_replay_status");
  if (!r.ok) {
    throw new Error(`GET /api/track_replay_status failed: ${r.status}`);
  }
  const parsed = TrackReplayStatusSchema.parse(await r.json());
  return { available: parsed.available, tracks: parsed.tracks };
}


// Phase 1A wire shape for POST /api/tests/{id}/runs. source_event is
// NOT a body field — Phase 1's invariant is one source_event per Test,
// set at Test creation. The backend reads it from the parent Test row.
import { RunSchema } from "./schemas";

// Phase 2 structured overlay: sparse — only edited corners/fields
// appear. Missing fields inherit from the Test's baseline XLSM at
// synth time. Validated server-side by DamperSettingPayload with
// extra='forbid'.
export type DamperCornerEdit = {
  damper_code?: string | null;
  clicks?: [number, number, number, number] | null;
};
export type DamperSettingPayload = {
  corners?: Partial<Record<"lf" | "rf" | "lr" | "rr", DamperCornerEdit>>;
};

export type CreateRunBody = {
  baseline_run_id?: number | null;
  // Structured payload, or empty object for "replay baseline".
  damper_setting?: DamperSettingPayload | Record<string, never>;
  // Dedup-key salt for explicit re-runs of the same inputs. Bump on
  // Retry / Re-run baseline so a new Run row gets created instead of
  // the existing one being returned.
  retry_n?: number | null;
  mode?: string | null;
};

export async function createRun(testId: number, body: CreateRunBody = {}): Promise<Run> {
  const r = await fetch(`/api/tests/${testId}/runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`POST /api/tests/${testId}/runs failed: ${r.status}: ${text}`);
  }
  return RunSchema.parse(await r.json());
}

// Re-run the Reference run (R0) in place: the backend purges R0's
// artifacts and requeues its job so the runner recomputes it on current
// physics — the remedy when an AR start refuses a stale baseline
// (2026-06-11 replay-guardrails build). 409 while R0 is in flight or an
// AutoResearch session is active.
export async function rerunReference(testId: number): Promise<Run> {
  const r = await fetch(`/api/tests/${testId}/reference/rerun`, {
    method: "POST",
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(
      `POST /api/tests/${testId}/reference/rerun failed: ${r.status}: ${text}`,
    );
  }
  return RunSchema.parse(await r.json());
}

export async function cancelRun(runId: number): Promise<Run> {
  const r = await fetch(`/api/runs/${runId}/cancel`, { method: "POST" });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`POST /api/runs/${runId}/cancel failed: ${r.status}: ${text}`);
  }
  return RunSchema.parse(await r.json());
}

// Hard-delete a Test and everything hanging off it (runs, result
// artifacts, queued jobs, auto-research history) — the backend cascade
// handles the children. Irreversible. Throws on a non-2xx response.
export async function deleteTest(id: number): Promise<void> {
  const r = await fetch(`/api/tests/${id}`, { method: "DELETE" });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`DELETE /api/tests/${id} failed: ${r.status}: ${text}`);
  }
}
